import React, { useState } from "react";
import { Rows } from "./Rows";
import { useAtom } from "jotai";
import { activeChildDetail } from "../../AtomStore/store";
import { useRequestProcessor } from "../../requestProcessor";
import { getAttendance } from "../../QueryFunctions/queryfunction";

import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

const Attendance = () => {
  const { useQueryWrapper } = useRequestProcessor();
  const [activeChild] = useAtom(activeChildDetail);

  const [currentDate, setCurrentDate] = useState({
    month: new Date().getMonth(),
    year: new Date().getFullYear(),
  });

  const {
    data: attendanceList,
    isLoading,
    isFetching,
  } = useQueryWrapper(
    [
      "getAttendance",
      "attendance" + activeChild?.id,
      currentDate.year,
      currentDate.month,
    ],
    () => getAttendance()
  );

  const monthList = [
    { value: "January", days: 31 },
    { value: "February", days: currentDate.year % 4 === 0 ? 29 : 28 },
    { value: "March", days: 31 },
    { value: "April", days: 30 },
    { value: "May", days: 31 },
    { value: "June", days: 30 },
    { value: "July", days: 31 },
    { value: "August", days: 31 },
    { value: "September", days: 30 },
    { value: "October", days: 31 },
    { value: "November", days: 30 },
    { value: "December", days: 31 },
  ];

  const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const holidayRenderList = [];

  let currentDay = new Date().getDate();
  const firstDayOfMonth = new Date(currentDate.year, currentDate.month, 1);
  const startDay = firstDayOfMonth.getDay();
  const emptyDays = Array(startDay).fill("");

  const monthDays = Array.from(
    { length: monthList[currentDate.month].days },
    (_, i) => {
      const day = i + 1;
      const holiday = holidayRenderList.find(
        (ele) => new Date(ele.holiday_date).getDate() === day
      );

      const attendance = attendanceList?.find((ele) => {
        const attendanceDate = new Date(ele.date);
        return (
          attendanceDate.getDate() === day &&
          attendanceDate.getMonth() === currentDate.month &&
          attendanceDate.getFullYear() === currentDate.year
        );
      });

      const isCurrentDay =
        day === currentDay &&
        currentDate.month === new Date().getMonth() &&
        currentDate.year === new Date().getFullYear();

      const dayOfWeek = new Date(
        currentDate.year,
        currentDate.month,
        day
      ).getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

      return {
        value: day,
        isWeekend,
        bgColor: isCurrentDay
          ? "bg-gray-300 text-black font-bold"
          : holiday
          ? "bg-annual text-white"
          : "",
        status: attendance ? attendance.status : "",
        ...(holiday || {}),
      };
    }
  );

  const holidayCalendarDays = [...emptyDays, ...monthDays];

  const handleMonthChange = (direction) => {
    setCurrentDate((prevState) => {
      let newMonth = prevState.month + direction;
      let newYear = prevState.year;

      if (newMonth < 0) {
        newMonth = 11;
        newYear--;
      } else if (newMonth > 11) {
        newMonth = 0;
        newYear++;
      }

      return { month: newMonth, year: newYear };
    });
  };

  // ✅ Monthly attendance stats
  const attendanceForMonth = attendanceList?.filter((record) => {
    const recordDate = new Date(record.date);
    return (
      recordDate.getMonth() === currentDate.month &&
      recordDate.getFullYear() === currentDate.year
    );
  });

  const totalDays = attendanceForMonth?.length || 0;
  const presentDays = attendanceForMonth?.filter(
    (record) => record.status === "Present"
  ).length;

  const presentPercentage = totalDays
    ? Math.round((presentDays / totalDays) * 100)
    : 0;
  const absentPercentage = totalDays ? 100 - presentPercentage : 0;

  return (
    <div className="bg-white min-h-[100vh]">
      <div
        className={`container sm:px-1 md:px-10 lg:px-20 ${
          isLoading ? "animate-pulse" : ""
        }`}
      >
        {/* Month Navigation */}
        <div className="flex justify-between items-center mt-4 mb-2 sm:px-3 md:px-4 lg:px-8">
          <FaChevronLeft
            onClick={() => handleMonthChange(-1)}
            className="cursor-pointer hover:text-header transition"
          />
          <h3 className="font-bold">{`${monthList[currentDate.month].value} ${
            currentDate.year
          }`}</h3>
          <FaChevronRight
            onClick={() => handleMonthChange(1)}
            className="cursor-pointer hover:text-header transition"
          />
        </div>

        <Rows
          colCount={"grid-cols-7 mb-2"}
          colData={daysOfWeek}
          daysInMonth={false}
        />
        <Rows
          colCount={"grid-cols-7"}
          colStyle={"border-1 p-2 md:p-4"}
          colData={holidayCalendarDays}
          daysInMonth={true}
          currentDate={currentDate}
          isLoading={isLoading || isFetching}
        />
      </div>

      <div className="border-t-2 border-header mt-3 mx-20"></div>

      {/* Present/Absent Chips */}
      <div className="flex justify-center items-center gap-4 mt-3 flex-wrap">
        <div className="flex items-center gap-2 bg-green-600 text-white px-3 py-1 rounded-full font-semibold text-sm">
          <div className="size-2.5 bg-white rounded-full"></div>
          Present: {presentPercentage}%
        </div>
        <div className="flex items-center gap-2 bg-red-600 text-white px-3 py-1 rounded-full font-semibold text-sm">
          <div className="size-2.5 bg-white rounded-full"></div>
          Absent: {absentPercentage}%
        </div>
      </div>

      {/* Present Records Table */}
      <div className="xs:m-2 sm:m-2 md:mx-10 lg:mx-20 my-3 pb-4">
        <div className="gap-2 bg-header p-2 items-center justify-around rounded-t-md grid grid-cols-12">
          <h2 className="col-span-4 text-center text-white font-semibold">
            Date
          </h2>
          <h2 className="col-span-4 text-center text-white font-semibold">
            Check In
          </h2>
          <h2 className="col-span-4 text-center text-white font-semibold">
            Check Out
          </h2>
        </div>

        {isLoading || isFetching ? (
          [...Array(9)].map((_, ele) => (
            <div
              className="flex gap-2 p-2 items-center justify-around rounded-t-md"
              key={ele}
            >
              <div className="flex flex-col gap-2 items-center justify-center">
                <div className="h-5 w-5 rounded bg-slate-200"></div>
                <div className="h-2 w-8 rounded bg-slate-200"></div>
              </div>
              <div className="h-3 w-20 rounded bg-slate-200"></div>
              <div className="h-3 w-20 rounded bg-slate-200"></div>
            </div>
          ))
        ) : (
          <div className="overflow-y-auto max-h-74 border-1">
            {attendanceForMonth
              ?.filter((record) => record.status === "Present")
              .map((record, index) => {
                const date = new Date(record.date);
                const day = date.getDate();
                const weekday = daysOfWeek[date.getDay()];

                const formatTime = (time) => {
                  const [rawHours, minutes] = time.split(":");
                  const hours = parseInt(rawHours, 10);
                  const isPM = hours >= 12;
                  const formattedHours = hours % 12 || 12;
                  const paddedHours = String(formattedHours).padStart(2, "0");
                  return `${paddedHours}:${minutes} ${isPM ? "PM" : "AM"}`;
                };

                return (
                  <div
                    key={index}
                    className="gap-2 p-2 mt-1 items-center grid grid-cols-12"
                  >
                    <div className="text-center col-span-4">
                      <h2 className="text-lg font-semibold leading-4">
                        {day}
                        <br />
                        <span className="text-sm font-normal">{weekday}</span>
                      </h2>
                    </div>
                    <h2 className="text-center col-span-4">
                      {record.time_in !== "00:00:00"
                        ? formatTime(record.time_in)
                        : "--"}
                    </h2>
                    <h2 className="col-span-4 text-center">
                      {record.time_out !== "00:00:00"
                        ? formatTime(record.time_out)
                        : "--"}
                    </h2>
                  </div>
                );
              })}

            {attendanceForMonth?.filter((record) => record.status === "Present")
              .length === 0 && (
              <div className="text-center text-gray-500 mt-4 mb-4">
                No present records found for this month.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Attendance;
